/**
 * One random key per attachment; the server stores a blob it cannot open
 * (SEC-04, chat-message-lifecycle §7в).
 *
 * The key never touches the server on its own: it rides inside the E2EE
 * message envelope (`e.file.k`), so whoever can read the message — and
 * nobody else — can open the file. The blob starts with a version byte,
 * because a wire format without one has already cost a release (бриф §5).
 */
import nacl from 'tweetnacl';

export const FILE_SEAL_V1 = 0x01;
const NONCE_LEN = 24;
const KEY_LEN = 32;
const OVERHEAD = 1 + NONCE_LEN + nacl.secretbox.overheadLength;

const b64 = (u8) => btoa(String.fromCharCode(...u8));
function unb64(s) {
  try {
    const bin = atob(s); const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch (e) { return null; }
}

/** Encrypt file bytes under a fresh random key. Returns the wire blob and the key for the envelope. */
export function sealFile(bytes) {
  const key = nacl.randomBytes(KEY_LEN);
  const nonce = nacl.randomBytes(NONCE_LEN);
  const box = nacl.secretbox(bytes, nonce, key);
  const sealed = new Uint8Array(1 + NONCE_LEN + box.length);
  sealed[0] = FILE_SEAL_V1;
  sealed.set(nonce, 1);
  sealed.set(box, 1 + NONCE_LEN);
  return { sealed, keyB64: b64(key) };
}

/** Decrypt a sealed blob. Null on anything wrong — version, key, MAC. */
export function openFile(bytes, keyB64) {
  if (!bytes || bytes.length < OVERHEAD || bytes[0] !== FILE_SEAL_V1) return null;
  const key = unb64(keyB64);
  if (!key || key.length !== KEY_LEN) return null;
  return nacl.secretbox.open(bytes.slice(1 + NONCE_LEN), bytes.slice(1, 1 + NONCE_LEN), key);
}
